import type { ComponentProps } from 'react';

import type { VsImageProps } from '@vs-shop/headless/useVsImage';
import Image from '@vs-shop/image-link/Image';
import { clsxm } from '@vs-shop/utils';

/**
 * A video where the gallery otherwise shows a photograph — on the page's stage
 * and on the lightbox's alike, which is why these parts are neither section's.
 *
 *   <ProductGalleryVideoPoster role="button" tabIndex={0} onClick={…} aria-label={playLabel}>
 *     <ProductGalleryVideoPosterImage src={stillSrc} alt={title} fill onError={…} />
 *     <ProductGalleryVideoMark>{playArtwork}</ProductGalleryVideoMark>
 *   </ProductGalleryVideoPoster>
 *
 *   <ProductGalleryVideo>
 *     <ProductGalleryVideoMount id={playerId} />
 *   </ProductGalleryVideo>
 *
 *   <ProductGalleryVideoMessage>{copy}</ProductGalleryVideoMessage>
 *
 * What plays, and how, is the tenant's: which host the medium is on, the embed
 * URL, the player's own script and its lifecycle, and every string. This module
 * ships the frame that player mounts into, the still it replaces, the mark that
 * says the still is playable, and the box a message fills — the parts that have
 * to match the photographs beside them.
 *
 * No part here takes a `variant`: a video is framed the same way wherever the
 * gallery shows one, and what differs between the two surroundings is the box
 * it is handed.
 */

/**
 * The video stage — the frame a player fills once a shopper asks for it.
 *
 * What plays, and how, is the tenant's: which host the medium is on, the embed
 * URL, the player's own script and its lifecycle. This package ships the frame
 * that player mounts into, the still it replaces, and the mark that says the
 * still is playable — the parts that have to match the photographs beside them.
 */
const productGalleryVideoClasses = 'relative h-full w-full';

export type ProductGalleryVideoProps = ComponentProps<'div'>;

/** The frame a video's still or its player fills. */
export const ProductGalleryVideo = ({ className, ...props }: ProductGalleryVideoProps) => (
  <div
    data-slot="product-gallery-video"
    className={clsxm(productGalleryVideoClasses, className)}
    {...props}
  />
);

/**
 * Where a player mounts. Its own part rather than the frame's anatomy because a
 * player replaces the element it is given, and the tenant's player needs to name
 * that element — the `id` rides the native prop spread.
 */
const productGalleryVideoMountClasses = 'absolute inset-0 h-full w-full';

export type ProductGalleryVideoMountProps = ComponentProps<'div'>;

/** The element a tenant's player takes over. */
export const ProductGalleryVideoMount = ({
  className,
  ...props
}: ProductGalleryVideoMountProps) => (
  <div
    data-slot="product-gallery-video-mount"
    className={clsxm(productGalleryVideoMountClasses, className)}
    {...props}
  />
);

/**
 * The still, before anyone has asked for the video. It is the whole click
 * target, so what makes it activatable — the role, the tab stop, the keys —
 * rides the native prop spread from the tenant that owns the activation.
 */
const productGalleryVideoPosterClasses = 'group relative h-full w-full cursor-pointer';

export type ProductGalleryVideoPosterProps = ComponentProps<'div'>;

/** The video's still, and the surface that starts it. */
export const ProductGalleryVideoPoster = ({
  className,
  ...props
}: ProductGalleryVideoPosterProps) => (
  <div
    data-slot="product-gallery-video-poster"
    className={clsxm(productGalleryVideoPosterClasses, className)}
    {...props}
  />
);

/**
 * The still's imagery — cropped rather than contained, unlike every photograph
 * in this section: a video's still is a frame of the video, and letterboxing it
 * inside a stage that is already letterboxing the player reads as a mistake.
 */
const productGalleryVideoPosterImageClasses = 'object-cover';

/** The same seam and the same rules as the section's other media parts. */
export type ProductGalleryVideoPosterImageProps = VsImageProps;

/** The still filling {@link ProductGalleryVideoPoster}. */
export const ProductGalleryVideoPosterImage = ({
  className,
  ...props
}: ProductGalleryVideoPosterImageProps) => (
  <Image className={clsxm(productGalleryVideoPosterImageClasses, className)} {...props} />
);

/**
 * The mark centred over a video's still. How large it reads against the still is
 * this section's judgement — the same answer it gives for the discount medallion
 * over a photograph — so the tenant's artwork carries no size and fills this box.
 */
const productGalleryVideoMarkClasses = 'lap:size-20 absolute inset-1/2 size-16 -translate-1/2';

export type ProductGalleryVideoMarkProps = ComponentProps<'div'>;

/** Where the play mark hangs over a still. */
export const ProductGalleryVideoMark = ({ className, ...props }: ProductGalleryVideoMarkProps) => (
  <div
    data-slot="product-gallery-video-mark"
    className={clsxm(productGalleryVideoMarkClasses, className)}
    {...props}
  />
);

/**
 * What the stage says when there is no video to show — an unreadable address, a
 * player that would not load. One part for both, because they are the same
 * object: the stage's own box carrying a line of the tenant's copy.
 *
 * The muted colour is set here and inherited, so the copy inside carries no
 * class of its own.
 */
const productGalleryVideoMessageClasses =
  'bg-background-intense text-foreground-muted flex h-full w-full items-center justify-center';

export type ProductGalleryVideoMessageProps = ComponentProps<'div'>;

/** The stage with a message in place of a video. */
export const ProductGalleryVideoMessage = ({
  className,
  ...props
}: ProductGalleryVideoMessageProps) => (
  <div
    data-slot="product-gallery-video-message"
    className={clsxm(productGalleryVideoMessageClasses, className)}
    {...props}
  />
);
